/**
 * Accept Invitation Page
 *
 * Handles both platform invitations (new user registration) and
 * group/form invitations (existing user accepting invite).
 */

import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import PasswordStrengthMeter from '../../components/auth/PasswordStrengthMeter';

interface InvitationInfo {
  valid: boolean;
  type: 'platform' | 'group' | 'form';
  email: string;
  role?: string;
  groupName?: string;
  formName?: string;
  invitedBy: string;
  expiresAt: string;
  requiresRegistration: boolean;
}

export function AcceptInvitationPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();

  const [isLoading, setIsLoading] = useState(true);
  const [invitation, setInvitation] = useState<InvitationInfo | null>(null);
  const [error, setError] = useState('');

  // Registration form (for new users)
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');

  // Acceptance state
  const [isAccepting, setIsAccepting] = useState(false);
  const [acceptSuccess, setAcceptSuccess] = useState(false);

  // Validate invitation on mount
  useEffect(() => {
    const validateInvitation = async () => {
      if (!token) {
        setError('No invitation token provided');
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(`/api/invitations/accept/${token}`);
        const data = await response.json() as InvitationInfo & { error?: string };

        if (!response.ok) {
          setError(data.error || 'Invalid invitation');
        } else if (data.valid) {
          setInvitation(data);
        } else {
          setError('This invitation is no longer valid');
        }
      } catch {
        setError('Failed to validate invitation');
      } finally {
        setIsLoading(false);
      }
    };

    validateInvitation();
  }, [token]);

  const handleAccept = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation for new users
    if (invitation?.requiresRegistration) {
      // Validate display name
      if (!displayName || displayName.trim().length === 0) {
        setError('Display name is required');
        return;
      }
      if (displayName.trim().length < 2 || displayName.trim().length > 100) {
        setError('Display name must be between 2 and 100 characters');
        return;
      }

      if (password !== confirmPassword) {
        setError('Passwords do not match');
        return;
      }
      if (password.length < 12) {
        setError('Password must be at least 12 characters');
        return;
      }
    }

    setIsAccepting(true);

    try {
      const body: Record<string, string> = {};
      if (invitation?.requiresRegistration) {
        body.password = password;
        body.displayName = displayName.trim();
      }

      const response = await fetch(`/api/invitations/accept/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        credentials: 'include',
      });

      const data = await response.json() as { error?: string; redirectTo?: string };

      if (!response.ok) {
        setError(data.error || 'Failed to accept invitation');
      } else {
        setAcceptSuccess(true);
        // Redirect after short delay
        setTimeout(() => {
          if (invitation?.requiresRegistration) {
            navigate('/login');
          } else {
            navigate(data.redirectTo || '/formspace');
          }
        }, 2000);
      }
    } catch {
      setError('Failed to accept invitation');
    } finally {
      setIsAccepting(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Validating invitation...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
        <div className="max-w-md w-full text-center">
          <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Invalid Invitation</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="space-y-3">
            <Link
              to="/login"
              className="block w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Go to Login
            </Link>
            <Link
              to="/register"
              className="block w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Create an Account
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (acceptSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
        <div className="max-w-md w-full text-center">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Invitation Accepted!</h2>
          <p className="text-gray-600 mb-4">
            {invitation?.requiresRegistration
              ? 'Your account has been created. Redirecting to login...'
              : 'You have successfully joined. Redirecting...'}
          </p>
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
        </div>
      </div>
    );
  }

  // Main invitation view
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-lg shadow-lg p-8">
          {/* Header */}
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900">
              {invitation?.type === 'platform'
                ? "You're Invited!"
                : invitation?.type === 'group'
                ? 'Group Invitation'
                : 'Form Access Invitation'}
            </h1>
            <p className="mt-2 text-gray-600">
              {invitation?.invitedBy} has invited you
              {invitation?.type === 'group' && invitation?.groupName && (
                <> to join <strong>{invitation.groupName}</strong></>
              )}
              {invitation?.type === 'form' && invitation?.formName && (
                <> to access <strong>{invitation.formName}</strong></>
              )}
            </p>
          </div>

          {/* Invitation details */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-500">Email</dt>
                <dd className="font-medium text-gray-900">{invitation?.email}</dd>
              </div>
              {invitation?.role && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">Role</dt>
                  <dd className="font-medium text-gray-900 capitalize">{invitation.role}</dd>
                </div>
              )}
              <div className="flex justify-between">
                <dt className="text-gray-500">Expires</dt>
                <dd className="font-medium text-gray-900">
                  {new Date(invitation?.expiresAt || '').toLocaleDateString()}
                </dd>
              </div>
            </dl>
          </div>

          {/* Error message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-6">
              {error}
            </div>
          )}

          <form onSubmit={handleAccept}>
            {/* Registration fields for new users */}
            {invitation?.requiresRegistration && (
              <div className="space-y-4 mb-6">
                <div>
                  <label htmlFor="displayName" className="block text-sm font-medium text-gray-700">
                    Display Name
                  </label>
                  <input
                    id="displayName"
                    type="text"
                    required
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Your full name"
                  />
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                    Create Password
                  </label>
                  <input
                    id="password"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Create a strong password"
                  />
                  <PasswordStrengthMeter password={password} />
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                    Confirm Password
                  </label>
                  <input
                    id="confirmPassword"
                    type="password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={`mt-1 block w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      confirmPassword && password !== confirmPassword
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                    }`}
                    placeholder="Confirm your password"
                  />
                  {confirmPassword && password !== confirmPassword && (
                    <p className="mt-1 text-sm text-red-600">Passwords do not match</p>
                  )}
                </div>

                <p className="text-xs text-gray-500">
                  By accepting, you agree to our{' '}
                  <Link to="/terms" className="text-blue-600 hover:underline">
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy" className="text-blue-600 hover:underline">
                    Privacy Policy
                  </Link>
                  .
                </p>
              </div>
            )}

            {/* Already logged in as different user */}
            {isAuthenticated && user?.email !== invitation?.email && (
              <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded-lg text-sm mb-6">
                You are currently logged in as <strong>{user?.email}</strong>.
                This invitation is for <strong>{invitation?.email}</strong>.
                Please log out first or use a different browser.
              </div>
            )}

            {/* Accept button */}
            <button
              type="submit"
              disabled={isAccepting || (isAuthenticated && user?.email !== invitation?.email)}
              className="w-full px-4 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isAccepting ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Accepting...
                </span>
              ) : invitation?.requiresRegistration ? (
                'Create Account & Accept'
              ) : (
                'Accept Invitation'
              )}
            </button>

            {/* Decline option */}
            <p className="mt-4 text-center text-sm text-gray-500">
              Don't want to accept?{' '}
              <Link to="/" className="text-blue-600 hover:underline">
                Go back home
              </Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AcceptInvitationPage;
